import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { checkDeviceDescriptionConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import { UpdateComponentConfig } from "../../utils/api.js"

import useComponentConfigData, { componentConfigData, modified } from "../../hooks/useComponentConfigData.js"
import useDeviceSchemas from "../../hooks/useDeviceSchemas.js"
import useUtgData from "../../hooks/useUtgData";

export default function DeviceDetail() {

    const navigate = useNavigate();
    const { beId, componentId, name } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const desc = searchParams.get('desc')
    const editMode = !!desc
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()

    const { utg, loading: utgLoading, error: utgError } = useUtgData(beId, componentId, config, accessToken)
    const { loading: componentConfigLoading, modified } = useComponentConfigData(beId, utg.value, config, accessToken)
    const device = useComputed(() => {
        if (componentConfigData.value.componentConfig && editMode) {
            const deviceList = componentConfigData.value.componentConfig?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return {}

            const index = componentConfigData.value.componentConfig.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            const currentDevice = componentConfigData.value.componentConfig.config.deviceConfig.device[index]
            const jsonFormDevice = { config: currentDevice }
            return jsonFormDevice
        }
        return null
    })
    const deviceIndex = useComputed(() => {
        if (componentConfigData.value.componentConfig && editMode) {
            const deviceList = componentConfigData.value.componentConfig?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return -1

            const index = componentConfigData.value.componentConfig.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            return index
        }
        return -1
    })

    const { deviceSchemas, loading: deviceSchemaLoading } = useDeviceSchemas(config, accessToken)
    const deviceSchema  = useComputed(() => {
        // deviceSchemas now contains all device schemas. Find the correct one to load into jsonForm
        // note that name/deviceType is the only common field we have between the schema and the data
        // this is passed into the page as a query parameter
        if (deviceSchemas && deviceSchemas.value && deviceSchemas.value.length) {
            // const schemaResult = { config: deviceSchemas.value.find(a => a.name == name).schemaValue }
            const schema = deviceSchemas.value.find(a => a.name == name)
            const schemaResult = schema.schemaValue
            return schemaResult
        }
        return {}
    })
    
    const pageTitle = useComputed(() => {
        return editMode ?
        `Manage ${name} UTG Device` :
        `Add ${name} UTG Device`
    })

    
    const jsonFormData = useComputed(() => {

        if (isEmptyObject(deviceSchema.value) || 
            isEmptyObject(componentConfigData.value)) {
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(deviceSchema.value)

        // disable editing of all fields if canEditSignal is false
        if (!canEditSignal.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }

        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'CONFIG'
        console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
        const formData = {
            schema: clonedSchema.properties.config,
            loadedComponentConfig: componentConfigData.value.componentConfig.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditSignal.value
        }
        if(device.value) {
            formData.value = device.value.config
        }
        console.log('formData from config', formData)
        return formData
    }) 

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const isFormSubmitting = useSignal(false)
    const accordionData = [
        { name: 'Device', data: device },
        { name: 'Device Index', data: deviceIndex },
        { name: 'Device Schema', data: deviceSchema },
        { name: 'All Device Schemas', data: deviceSchemas },
        { name: 'Component Config', data: componentConfigData },
        { name: 'UTG Data', data: utg },
        { name: 'Access Token', data: accessToken},
        { name: 'App Config', data: config}
    ]
   
    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmitting.value) {
            return false
        }
        isFormSubmitting.value = true

        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: componentConfigData.value.componentConfig.config,
                    hash: componentConfigData.value.componentConfig.hash,
                    isCommitted: componentConfigData.value.componentConfig.isCommitted,
                    lastUpdatedAt: componentConfigData.value.componentConfig.lastUpdatedAt,
                    lastUpdatedBy: componentConfigData.value.componentConfig.lastUpdatedBy,
                    revisionId: componentConfigData.value.componentConfig.revisionId,
                    componentId: componentConfigData.value.componentConfig.componentId
                }

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.deviceConfig.device[deviceIndex.value] = values
                }
                else {
                    // insert values into the apiInterface array
                    data.config.deviceConfig.device.push(values)
                }

                // validate taskDesc uniqueness in case jsonform validation fails
                if (checkDeviceDescriptionConflicts(data.config.deviceConfig.device)) {
                    addNotification("Unable to save due to conflict. All Devices must have a unique Device Name.", NotificationTypes.ERROR)
                    return false
                }

                console.log(data)
                UpdateComponentConfig(beId, utg.value.MachinePermanentId, utg.value.ComponentPermanentId, data, config, accessToken)
                    .then(result => {
                        // componentConfigData.value = result
                        modified.value = true
                        addNotification('Devices was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Device.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmitting.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        console.log('handleFormChange')
        isFormEdited.value = true
    }

    if (utgLoading.value || componentConfigLoading.value || deviceSchemaLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../devices')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                UTG List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title={pageTitle.value} canEditSignal={canEditSignal} />
            <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
        </Stack>
        <UtgNav beId={beId} componentId={componentId} activeKey="3"></UtgNav>
        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}